import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function QuestionnairePage({ onSubmit, onBack, preSelectedCategory }) {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  
  const [formData, setFormData] = useState({
    useCase: preSelectedCategory || '',
    outcome: '',
    experienceLevel: '',
    targetAudience: '',
    tone: '',
    constraints: ''
  });

  const progressPercentage = (currentStep / totalSteps) * 100;

  const isImagePrompt = formData.useCase === 'Image Generation';
  const isVideoPrompt = formData.useCase === 'Video Creation';
  const isCodePrompt = formData.useCase === 'Code Development';
  
  // Skip step 4 (target audience) for image prompts
  const shouldSkipStep4 = isImagePrompt;

  const handleNext = () => {
    // Validate current step
    if (currentStep === 1 && !formData.useCase) {
      toast.error('Please select a use case');
      return;
    }
    if (currentStep === 2 && !formData.outcome.trim()) {
      toast.error('Please describe your desired outcome');
      return;
    }
    if (currentStep === 3 && !formData.experienceLevel) {
      toast.error('Please select your experience level');
      return;
    }
    if (currentStep === 4 && !shouldSkipStep4 && !formData.targetAudience.trim()) {
      toast.error('Please describe your target audience');
      return;
    }
    if (currentStep === 5 && !formData.tone) {
      toast.error('Please select a tone');
      return;
    }
    
    // Skip step 4 if it's an image prompt
    if (currentStep === 3 && shouldSkipStep4) {
      setCurrentStep(5);
    } else if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Submit form
      onSubmit(formData);
      toast.success('Generating your custom prompt!');
    }
  };
  
  const handleBack = () => {
    // Handle back navigation with step 4 skip
    if (currentStep === 5 && shouldSkipStep4) {
      setCurrentStep(3);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <nav className="border-b bg-card/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-8 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-tight">PromptCraft AI</span>
                <span className="text-xs text-muted-foreground hidden sm:block">by Growthyfai</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-2 flex justify-between text-sm">
          <span className="text-muted-foreground">Step {currentStep} of {totalSteps}</span>
          <span className="font-medium text-primary">{Math.round(progressPercentage)}% Complete</span>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl animate-scale-in">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl">
              {currentStep === 1 && 'What is your primary use case?'}
              {currentStep === 2 && 'What outcome are you trying to achieve?'}
              {currentStep === 3 && 'What is your experience level?'}
              {currentStep === 4 && 'Who is your target audience?'}
              {currentStep === 5 && 'What tone or style do you prefer?'}
              {currentStep === 6 && 'Any specific constraints or requirements?'}
            </CardTitle>
            <CardDescription className="text-base">
              {currentStep === 1 && 'Select the category that best describes your needs'}
              {currentStep === 2 && 'Be as specific as possible about what you want to accomplish'}
              {currentStep === 3 && 'This helps us tailor the complexity and depth of your prompt'}
              {currentStep === 4 && 'Describe who will benefit from or consume the output'}
              {currentStep === 5 && 'Choose the communication style that fits your brand'}
              {currentStep === 6 && 'Optional: Add any specific requirements, limitations, or preferences'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Use Case */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <Label htmlFor="useCase" className="text-base">Select Your Use Case</Label>
                <Select value={formData.useCase} onValueChange={(value) => updateFormData('useCase', value)}>
                  <SelectTrigger id="useCase" className="h-12">
                    <SelectValue placeholder="Choose a category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Content Creation">✍️ Content Creation</SelectItem>
                    <SelectItem value="Image Generation">🎨 Image Generation</SelectItem>
                    <SelectItem value="Video Creation">🎬 Video Creation</SelectItem>
                    <SelectItem value="Code Development">💻 Code Development</SelectItem>
                    <SelectItem value="Digital Marketing">🎯 Digital Marketing</SelectItem>
                    <SelectItem value="Business Growth">📈 Business Growth</SelectItem>
                    <SelectItem value="Freelancing">💼 Freelancing</SelectItem>
                    <SelectItem value="E-commerce">🛒 E-commerce</SelectItem>
                    <SelectItem value="Customer Support">💬 Customer Support</SelectItem>
                    <SelectItem value="Data Analysis">📊 Data Analysis</SelectItem>
                    <SelectItem value="Social Media Management">📱 Social Media Management</SelectItem>
                    <SelectItem value="SEO & Content Strategy">🔍 SEO & Content Strategy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 2: Outcome */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <Label htmlFor="outcome" className="text-base">Describe Your Desired Outcome</Label>
                <Textarea
                  id="outcome"
                  placeholder={
                    isImagePrompt 
                      ? "Example: Create a photorealistic portrait of a warrior in fantasy armor with dramatic lighting..."
                      : isVideoPrompt
                      ? "Example: Create a 30-second promotional video showcasing our new product with energetic music..."
                      : isCodePrompt
                      ? "Example: Build a responsive login page with form validation and password strength indicator..."
                      : "Example: I want to create engaging blog posts that rank well on Google and convert readers into newsletter subscribers..."
                  }
                  value={formData.outcome}
                  onChange={(e) => updateFormData('outcome', e.target.value)}
                  className="min-h-[200px] text-base"
                />
                <p className="text-sm text-muted-foreground">
                  {isImagePrompt && "💡 Tip: Describe the subject, style, lighting, colors, and composition you want!"}
                  {isVideoPrompt && "💡 Tip: Include duration, style, mood, and key scenes you envision!"}
                  {isCodePrompt && "💡 Tip: Specify the technology stack, features, and functionality you need!"}
                  {!isImagePrompt && !isVideoPrompt && !isCodePrompt && "💡 Tip: The more specific you are, the better your prompt will be!"}
                </p>
              </div>
            )}

            {/* Step 3: Experience Level */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <Label htmlFor="experienceLevel" className="text-base">Your Experience Level</Label>
                <Select value={formData.experienceLevel} onValueChange={(value) => updateFormData('experienceLevel', value)}>
                  <SelectTrigger id="experienceLevel" className="h-12">
                    <SelectValue placeholder="Choose your level..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">🌱 Beginner - Just getting started</SelectItem>
                    <SelectItem value="Intermediate">🚀 Intermediate - Some experience</SelectItem>
                    <SelectItem value="Advanced">⭐ Advanced - Highly experienced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 4: Target Audience (Skip for Image Generation) */}
            {currentStep === 4 && !shouldSkipStep4 && (
              <div className="space-y-4">
                <Label htmlFor="targetAudience" className="text-base">Target Audience</Label>
                <Textarea
                  id="targetAudience"
                  placeholder="Example: Small business owners aged 30-50 who are looking to scale their operations and adopt digital tools..."
                  value={formData.targetAudience}
                  onChange={(e) => updateFormData('targetAudience', e.target.value)}
                  className="min-h-[150px] text-base"
                />
              </div>
            )}

            {/* Step 5: Tone */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <Label htmlFor="tone" className="text-base">Preferred Tone</Label>
                <Select value={formData.tone} onValueChange={(value) => updateFormData('tone', value)}>
                  <SelectTrigger id="tone" className="h-12">
                    <SelectValue placeholder="Choose a tone..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Professional">👔 Professional - Formal and authoritative</SelectItem>
                    <SelectItem value="Casual">😊 Casual - Friendly and approachable</SelectItem>
                    <SelectItem value="Creative">🎨 Creative - Innovative and expressive</SelectItem>
                    <SelectItem value="Technical">⚙️ Technical - Precise and detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 6: Constraints */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <Label htmlFor="constraints" className="text-base">Constraints or Requirements (Optional)</Label>
                <Textarea
                  id="constraints"
                  placeholder="Example: Must be under 500 words, needs to include specific keywords, should avoid technical jargon..."
                  value={formData.constraints}
                  onChange={(e) => updateFormData('constraints', e.target.value)}
                  className="min-h-[150px] text-base"
                />
                <p className="text-sm text-muted-foreground">You can skip this if you don't have any specific constraints</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {currentStep === 1 ? 'Cancel' : 'Previous'}
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 h-12"
              >
                {currentStep === totalSteps ? 'Generate Prompt' : 'Next'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
